import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { get, isString, map } from 'lodash-es';
import { filter, identity, noop, Observable, switchMap, tap } from 'rxjs';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../components/error-dialog/error-dialog.component';
import { PromptDialogComponent } from '../components/prompt-dialog/PromptDialog.component';
import { WaitDialogComponent } from '../components/wait-dialog/wait-dialog.component';

export interface ConfirmButton {
    title: string;
    color: 'primary' | 'accent' | 'warn' | '';
    value: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private dialog = inject(MatDialog);


  wait(message?: string): MatDialogRef<WaitDialogComponent> {
    return this.dialog.open(WaitDialogComponent, { disableClose: true, data: { title: message } });
  }

  confirm(message: string, title?: string, buttons?: ConfirmButton[]): Observable<boolean> {
    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: title,
          message: message,
          buttons: buttons || [
            { title: 'Cancel', color: 'primary', value: false },
            { title: 'OK', color: 'primary', value: true },
          ],
        },
      })
      .afterClosed();
  }

  prompt(label: string, options?: {
    title?: string;
    message?: string;
    icon?: string;
    value?: string;
  }): Observable<string> {
    return this.dialog.open(PromptDialogComponent, { data: {
      message: get(options, 'message', ''),
      label,
      title: get(options, 'title', ''),
      icon: get(options, 'icon', ''),
      value: get(options, 'value', '')
    } }).afterClosed();
  }


  error(message: string, error?: unknown, data?: unknown) {
    return this.dialog
      .open(ErrorDialogComponent, {
        data: {
          message,
          error,
          data,
        }
      }).afterClosed();
  }

  alert(message: string, title?: string): Observable<boolean> {
    return this.confirm(message, title, [{ title: 'OK', color: 'primary', value: true }]);
  }

  /**
   * Open a dialog, wait for the result, and then perform an action.
   * @param component The dialog to open
   * @param config The dialog configuration, including data if needed
   * @param waitMessage The message to display while waiting
   * @param action The action to perform after the dialog is closed, if the dialog  result is truthy
   * @param complete The action to perform after the action is complete
   * @param error The error message to display if the action fails, or a function to handle the error
   */
  action<T,Y>(
    component: ComponentType<unknown> | TemplateRef<unknown>,
    config: MatDialogConfig<unknown> | undefined,
    waitMessage: string,
    action: (result: T) => Observable<Y>,
    complete: (result: Y) => void,
    error: ((error: unknown) => void) | string
  ) {
    let wait = { close: noop };
    this.dialog.open(component, config).afterClosed().pipe(
      filter(identity),
      tap(() => wait = this.wait(waitMessage)),
      switchMap((result) => action(result)),
    ).subscribe({
      next: result => {
        wait.close();
        complete(result);
      },
      error: err => {
        wait.close();
        if (isString(error)) {
          this.error(error, err);
        } else {
          error(err);
        }
      }
    });

  }

  waitAction<Y>(
    waitMessage: string,
    action: () => Observable<Y>,
    complete: (result: Y) => void,
    error: ((error: unknown) => void) | string
  ) {
    const wait = this.wait(waitMessage);
    action().subscribe({
      next: result => {
        wait.close();
        complete(result);
      },
      error: err => {
        wait.close();
        if (isString(error)) {
          this.error(error, err);
        } else {
          error(err);
        }
      }
    });
  }

  confirmAction<Y>(
    confirmMessage: string,
    buttons: string[],
    waitMessage: string,
    action: () => Observable<Y>,
    complete: (result: Y) => void,
    error: ((error: unknown) => void) | string
  ) {
    this.action(
      ConfirmDialogComponent,
      {
        data: {
          message: confirmMessage,
          buttons: map(buttons, (title, index) => ({ title, color: 'primary', value: index > 0 })),
        }
      },
      waitMessage,
      action,
      complete,
      error
    );
  }
}
